import React, { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useMatch } from 'react-router-dom';
import paymentStyles from '../../../../stylesheets/portals/modules/tenant_portal/payments/payments.module.scss';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import SubmitSpinner from '../../components/SubmitSpinner';
import { useGlobalContext } from '../../components/GlobalContextProvider';
import interactionLockStyles from '../../../../stylesheets/portals/modules/tenant_portal/interaction_lock/interactionLock.module.scss';
import {
  PortalInteractionPresentation,
  usePortalInteractionLock,
} from './PortalInteractionLockContext';

interface ProcessingOverlayProps {
  tenantPortalBodyRef: RefObject<HTMLDivElement | null>;
}

const ProcessingOverlay = ({ tenantPortalBodyRef }: ProcessingOverlayProps) => {
  const paymentRoute = useMatch('/tenant-portal/lease/:leaseId/:page');
  const isPayment = [
    'review-and-pay',
    'enable-autopay',
    'new-payment-method',
  ].includes(paymentRoute?.params.page || '');
  const {
    activePresentation,
    activeStatus,
    isInteractionLocked,
    isOverlayVisible,
  } = usePortalInteractionLock();
  const { theme } = useGlobalContext();
  const overlayRef = useRef<HTMLDivElement>(null);
  const shouldRenderOverlay =
    isInteractionLocked &&
    activePresentation !== PortalInteractionPresentation.INLINE;

  useEffect(() => {
    if (!shouldRenderOverlay) return;

    const tenantPortalBody = tenantPortalBodyRef.current;
    if (!tenantPortalBody) {
      throw new Error('Tenant portal body is unavailable for interaction lock');
    }

    const previouslyFocusedElement = document.activeElement;
    tenantPortalBody.inert = true;
    document.body.classList.add(interactionLockStyles.bodyLocked);
    overlayRef.current?.focus();

    return () => {
      tenantPortalBody.inert = false;
      document.body.classList.remove(interactionLockStyles.bodyLocked);
      if (
        previouslyFocusedElement instanceof HTMLElement &&
        previouslyFocusedElement.isConnected
      ) {
        previouslyFocusedElement.focus();
      }
    };
  }, [shouldRenderOverlay, tenantPortalBodyRef]);

  if (!shouldRenderOverlay) return null;
  if (!activeStatus) {
    throw new Error('Locked tenant portal interaction is missing status text');
  }
  if (!isOverlayVisible) {
    return createPortal(
      <div
        ref={overlayRef}
        className={interactionLockStyles.visuallyHidden}
        role="status"
        aria-busy="true"
        aria-label={activeStatus}
        tabIndex={-1}
      >
        {activeStatus}
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={overlayRef}
      className={classNames(interactionLockStyles.overlay, {
        [paymentStyles.processing]: isPayment,
        [interactionLockStyles.darkOverlay]: theme === 'dark',
      })}
      data-test-class="tenant-portal-processing-overlay"
      role="dialog"
      aria-busy="true"
      aria-label={activeStatus}
      aria-modal="true"
      tabIndex={-1}
      onKeyDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div className={interactionLockStyles.content} role="status">
        <SubmitSpinner spinnerText={activeStatus} size={60} />
      </div>
    </div>,
    document.body
  );
};

export default ProcessingOverlay;
